import { useEffect, useId, useState, type ReactNode } from 'react';
import { CreditCard, Layers, ClipboardList, FilePenLine, Truck, ReceiptText } from 'lucide-react';
import {purchaseAgreement} from '../../../shared/purchaseAgreement';
import './PurchaseAgreement.css';

/** Present the existing agreement once, organized by subject without nested disclosures. */
export default function PurchaseAgreement({ productLine, separatelyQuotedSelections = [], compact = false, open = false, paymentMode = "deposit", children }: {
  children?: ReactNode;
  paymentMode?: "deposit" | "full";
  compact?: boolean;
  open?: boolean;
  productLine: 'basic' | 'custom';
  separatelyQuotedSelections?: string[];
}) {
  const id = useId();
  const [expanded, setExpanded] = useState(open);
  useEffect(()=>{if(open)setExpanded(true)},[open]);
  const {sections} = purchaseAgreement(productLine, separatelyQuotedSelections, paymentMode);
  if (compact) return <section className="purchase-agreement pa-compact" id="checkout-terms" aria-label="Purchase agreement">
    <details className="pa-disclosure" open={expanded} onToggle={event=>setExpanded(event.currentTarget.open)}>
      <summary>Purchase agreement<span>Read terms</span></summary>
      <div className="pa-fine-print" role="region" aria-label="Full purchase agreement">
        {sections.map(({key,title,paragraphs})=><section key={key}>
          <h4>{title}</h4>
          {paragraphs.map(([heading,body])=><p key={heading}><strong>{heading}.</strong> {body}</p>)}
        </section>)}
      </div>
    </details>
    {children && <div className="pa-consent">{children}</div>}
  </section>;
  const icons = [CreditCard, Layers, ClipboardList, FilePenLine, Truck, ReceiptText];
  function returnToSignature() {
    const input = document.getElementById('checkout-signer-name');
    input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    input?.focus({ preventScroll: true });
  }
  return <>
    <section className={`purchase-agreement${compact?' pa-compact':''}`} id="checkout-terms" aria-labelledby={`${id}-title`}>
      <header className="pa-header"><h3 id={`${id}-title`} tabIndex={-1}>Purchase agreement</h3></header>
      {!compact && <div className="pa-overview">
        {sections.map(({key,title,hint},index)=>{const Icon=icons[index];return <div className="pa-overview-item" key={key}>
          <Icon className="pa-icon" size={24} strokeWidth={1.6} aria-hidden="true"/>
          <div><h4>{title}</h4><p>{hint}</p></div>
        </div>})}
      </div>}
      <button type="button" className="pa-read-all" aria-expanded={expanded} aria-controls={`${id}-full`} onClick={()=>setExpanded(!expanded)}>{expanded?'Hide Full Agreement':'Read Full Agreement'}</button>
      <div id={`${id}-full`} className="pa-full" role="region" aria-label="Full purchase agreement" hidden={!expanded}>
        {sections.map(({key,title,paragraphs})=><section className="pa-full-section" key={key}>
          <h4>{title}</h4>
          {paragraphs.map(([heading,body])=><div className="pa-paragraph" key={heading}><h5>{heading}</h5><p>{body}</p></div>)}
        </section>)}
      </div>
    </section>
    {expanded && <button type="button" className="pa-return" onClick={returnToSignature}>Return to Signature ↑</button>}
  </>;
}


